import { useEffect, useState } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import { CommonTitle, thinScrollbar } from '../common'
import { Button } from '../Controls'
import CenterModal from './CenterModal'
import { CategoryProduct } from '../Shop/Categories'
import {
  useCheckout,
  useMediaQueries,
  useModal,
  useSessionStorage
} from 'shop/hooks'
import BottomModal from './BottomModal'
import { UpsellProductsList } from '../UpsellProducts/UpsellProductsList'

export type UpsellModalProps = {
  closeUpsellModal: () => void
}

const UpsellModal = ({ closeUpsellModal }: UpsellModalProps) => {
  const navigate = useNavigate()
  const { closeModal } = useModal()
  const { isMobile } = useMediaQueries()
  const { setSessionStorageItem } = useSessionStorage('upsell-modal')
  const { isModalOpen } = useModal()
  const { upsellProducts: contextUpsellProducts } = useCheckout()

  /** Local copy of upsell products from context  needed to support product removal animations in UpsellProductsList.
   * When a product is added to cart, it needs to stay in the DOM for ~1 second to play the fade-out/collapse animation
   * before being removed from the array. The child component filters products out after the animation completes */
  const [upsellProducts, setUpsellProducts] = useState(contextUpsellProducts)

  const handleNavToCheckout = () => {
    closeUpsellModal()
    closeModal('cart')
    navigate('/checkout')
  }

  useEffect(() => {
    if (!!contextUpsellProducts.length) {
      setUpsellProducts(contextUpsellProducts)
      setSessionStorageItem('viewed')
    } else {
      // if no products available, just continue to /checkout
      handleNavToCheckout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!upsellProducts.length) return <></>

  // Render different Modals based upon device
  return isMobile ? (
    <BottomModal
      handleCloseModal={closeUpsellModal}
      canClose={true}
      inModal={isModalOpen('cart')}
      modalHeight={'100%'}
    >
      <UpsellModalContent
        upsellProducts={upsellProducts}
        setUpsellProducts={setUpsellProducts}
        handleNavToCheckout={handleNavToCheckout}
      />
    </BottomModal>
  ) : (
    <CenterModal
      handleCloseModal={closeUpsellModal}
      styles={{ maxWidth: '560px' }}
      modalButtonVariant={'upsell'}
      inModal={isModalOpen('cart')}
    >
      <UpsellModalContent
        upsellProducts={upsellProducts}
        setUpsellProducts={setUpsellProducts}
        handleNavToCheckout={handleNavToCheckout}
      />
    </CenterModal>
  )
}

type UpsellModalContentProps = {
  upsellProducts: CategoryProduct[]
  setUpsellProducts: React.Dispatch<React.SetStateAction<CategoryProduct[]>>
  handleNavToCheckout: () => void
}

const UpsellModalContent = ({
  upsellProducts,
  setUpsellProducts,
  handleNavToCheckout
}: UpsellModalContentProps) => {
  return (
    <Container>
      <HeaderSection>
        <HeaderContent>
          <CommonTitle>Top Picks Customers Love</CommonTitle>
        </HeaderContent>
      </HeaderSection>
      <ProductsSection>
        <UpsellProductsList
          upsellProducts={upsellProducts}
          onProductsUpdate={setUpsellProducts}
          layout='grid'
          onAllProductsAdded={handleNavToCheckout}
        />
      </ProductsSection>
      <ModalActionsSection>
        <AllergenInfo>
          Allergen information can be found on our menu.
        </AllergenInfo>
        <Button onClick={handleNavToCheckout}>Go to checkout</Button>
      </ModalActionsSection>
    </Container>
  )
}

const Container = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    height: '100%',

    [theme.mediaQueries.viewport7]: {
      maxHeight: '90vh'
    }
  })
)

const HeaderSection = styled.div(
  ({ theme }): CSSObject => ({
    padding: '16px',
    borderBottom: '1px solid #D9D9D9',
    position: 'relative',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 10,

    [theme.mediaQueries.viewport7]: {
      position: 'initial'
    }
  })
)

const HeaderContent = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginRight: '20%'
  })
)

const ProductsSection = styled.div(
  ({ theme }): CSSObject => ({
    overflow: 'auto',
    padding: '4px 16px',
    backgroundColor: '#fff',
    ...(thinScrollbar(theme) as any),
    height: '100%',

    [theme.mediaQueries.viewport7]: {
      padding: '16px 24px',
      maxHeight: '600px'
    }
  })
)

const ModalActionsSection = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    zIndex: 10,
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    borderTop: '1px solid #D9D9D9',

    [theme.mediaQueries.viewport7]: {
      position: 'initial'
    }
  })
)

const AllergenInfo = styled.p(
  ({ theme }): CSSObject => ({
    fontSize: '14px',
    color: '#595959',
    margin: 0
  })
)

export default UpsellModal
